import React from 'react';
import { FileText, MoreHorizontal, Star, Trash2, FolderInput, Folder, Loader2, Calendar, Clock } from 'lucide-react';
import { Task } from '../services/task';
import { useLanguage } from '../i18nContext';
import { FolderWithExpanded } from '../hooks/useFolders';

interface TaskCardProps {
    task: Task;
    onOpen: (taskId: string) => void;
    onDelete: (taskId: string, title: string) => void;
    onToggleFavorite: (taskId: string, current: boolean) => void;
    onMoveToFolder: (taskId: string, folderId: string) => void;
    isMenuOpen: boolean;
    onMenuToggle: () => void;

    isDragging: boolean;
    onDragStart: (e: React.DragEvent, taskId: string) => void;
    onDragEnd: () => void;

    editingTaskId: string | null;
    editingTaskTitle: string;
    setEditingTaskTitle: (title: string) => void;
    startEditingTaskTitle: (taskId: string, currentTitle: string) => void;
    saveTaskTitle: () => void;
    cancelEditingTaskTitle: () => void;
    taskTitleInputRef: React.RefObject<HTMLInputElement>;

    showFolderPicker: string | null;
    setShowFolderPicker: (taskId: string | null) => void;
    folders: FolderWithExpanded[];
    displayFolderName: (name: string) => string;
}

export default function TaskCard({
    task, onOpen, onDelete, onToggleFavorite, onMoveToFolder,
    isMenuOpen, onMenuToggle, isDragging, onDragStart, onDragEnd,
    editingTaskId, editingTaskTitle, setEditingTaskTitle, startEditingTaskTitle, saveTaskTitle, cancelEditingTaskTitle, taskTitleInputRef,
    showFolderPicker, setShowFolderPicker, folders, displayFolderName
}: TaskCardProps) {
    const { language } = useLanguage();

    function calcProgress(currentStep: number, totalSteps: number): number {
        if (totalSteps <= 0) return 0;
        return Math.min(98, Math.floor((currentStep / totalSteps) * 100));
    }

    function getStepLabel(status: string, lang: 'zh' | 'en'): string {
        const labels: Record<string, [string, string]> = {
            'pending': ['准备中...', 'Preparing...'],
            'step1_prepare': ['正在识别语音...', 'Recognizing speech...'],
            'step1_asr': ['正在识别语音...', 'Recognizing speech...'],
            'step2_chunk': ['正在分析语义结构...', 'Analyzing structure...'],
            'step25_process': ['正在处理分段...', 'Processing segments...'],
            'step3_rewrite': ['Agent 正在改写内容...', 'Agent rewriting...'],
            'step4_integrate': ['正在整合纪要...', 'Integrating notes...'],
            'step5_mapping': ['正在匹配原文...', 'Mapping to source...'],
        };
        const pair = labels[status] || ['处理中...', 'Processing...'];
        return lang === 'zh' ? pair[0] : pair[1];
    }

    const createdDate = new Date(task.createdAt);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));

    let createdAtStr: string;
    if (diffDays === 0) createdAtStr = language === 'zh' ? '今天' : 'Today';
    else if (diffDays === 1) createdAtStr = language === 'zh' ? '昨天' : 'Yesterday';
    else if (diffDays < 7) createdAtStr = language === 'zh' ? `${diffDays} 天前` : `${diffDays} days ago`;
    else createdAtStr = createdDate.toLocaleDateString(language === 'zh' ? 'zh-CN' : 'en-US', { month: 'short', day: 'numeric' });

    const formatDuration = (seconds?: number): string => {
        if (!seconds) return '';
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        if (hours > 0) return language === 'zh' ? `${hours}小时 ${minutes}分` : `${hours}h ${minutes}m`;
        if (minutes > 0) return language === 'zh' ? `${minutes}分 ${secs.toString().padStart(2, '0')}秒` : `${minutes}m ${secs.toString().padStart(2, '0')}s`;
        return language === 'zh' ? `${secs}秒` : `${secs}s`;
    };

    const isProcessing = task.status !== 'completed' && task.status !== 'failed' && task.status !== undefined;
    const displayTitle = task.title || (language === 'zh' ? '未命名会议' : 'Untitled Meeting');
    const displaySummary = task.summary || (language === 'zh' ? '会议记录' : 'Meeting notes');
    const tags = [task.industry, task.company].filter(Boolean) as string[];
    const durationStr = formatDuration(task.durationSeconds);
    const taskKeywords = task.keywords || [];
    const speakers = task.speakers || [];

    return (
        <div
            data-card-id={task.taskId}
            onClick={(e) => {
                if (isProcessing || isDragging || editingTaskId === task.taskId) return;
                onOpen(task.taskId);
            }}
            className={`relative overflow-visible rounded-card transition-all duration-300 p-6 group ${isDragging ? 'opacity-50 scale-[0.98]' : ''} ${isProcessing ? 'bg-white border border-surface-border cursor-default' : 'bg-surface-light border border-transparent shadow-[0_2px_12px_rgba(0,0,0,0.02)] hover:shadow-deep hover:border-surface-border cursor-pointer'}`}
        >
            {isDragging && (
                <div className="absolute inset-0 rounded-card pointer-events-none" />
            )}
            {!isProcessing && (
                <div
                    draggable
                    onDragStart={(e) => { e.stopPropagation(); onDragStart(e, task.taskId); }}
                    onDragEnd={onDragEnd}
                    className="absolute top-0 left-0 opacity-0 group-hover:opacity-100 transition-all duration-200 z-20 cursor-grab active:cursor-grabbing"
                >
                    {/* CSS overflow:hidden 裁剪扇形，与 rounded-card(24px) 天然对齐，无需 SVG 手动凑角 */}
                    <div style={{ width: 36, height: 36, borderTopLeftRadius: 24, overflow: 'hidden' }}>
                        <svg width="36" height="36" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
                            {/* 完整扇形，由外层 div 的 overflow:hidden 裁去突出的直角 */}
                            <path
                                d="M 0 0 L 36 0 A 36 36 0 0 1 0 36 Z"
                                fill="rgba(0,0,0,0.04)"
                            />
                            {/* 6 个点排成 2×3，圆弧内侧重心位置 */}
                            <circle cx="10" cy="10" r="1.5" fill="rgba(0,0,0,0.30)" />
                            <circle cx="15" cy="10" r="1.5" fill="rgba(0,0,0,0.30)" />
                            <circle cx="10" cy="15" r="1.5" fill="rgba(0,0,0,0.30)" />
                            <circle cx="15" cy="15" r="1.5" fill="rgba(0,0,0,0.30)" />
                            <circle cx="10" cy="20" r="1.5" fill="rgba(0,0,0,0.18)" />
                            <circle cx="15" cy="20" r="1.5" fill="rgba(0,0,0,0.18)" />
                        </svg>
                    </div>
                </div>
            )}

            {!isProcessing && (
                <div className="absolute top-6 right-6 flex gap-2 opacity-0 group-hover:opacity-100 transition-all duration-200 transform translate-x-2 group-hover:translate-x-0 z-20">
                    <button
                        onClick={(e) => { e.stopPropagation(); onToggleFavorite(task.taskId, !!task.isFavorite); }}
                        className={`p-2 bg-white border shadow-sm rounded-lg transition-colors ${task.isFavorite ? 'border-yellow-300 text-yellow-500 bg-yellow-50' : 'border-surface-border text-ink-muted hover:bg-surface hover:text-yellow-500'}`}
                    >
                        <StarIcon filled={task.isFavorite ? 1 : 0} />
                    </button>

                    <div className="relative">
                        <button
                            onClick={(e) => { e.stopPropagation(); setShowFolderPicker(null); onMenuToggle(); }}
                            className={`p-2 bg-white border shadow-sm rounded-lg transition-all duration-200 ${isMenuOpen ? 'border-brand-900 text-brand-900 bg-surface' : 'border-surface-border text-ink-muted hover:bg-surface hover:text-brand-900 hover:scale-105 active:scale-95'}`}
                        >
                            <MoreHorizontal size={14} />
                        </button>

                        {isMenuOpen && (
                            <div className="absolute right-0 mt-2 w-48 bg-white border border-surface-border rounded-2xl shadow-xl z-50 overflow-visible animate-in fade-in zoom-in-95 duration-150 p-1 flex flex-col" onClick={(e) => e.stopPropagation()}>
                                <div className="relative">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setShowFolderPicker(showFolderPicker === task.taskId ? null : task.taskId); }}
                                        className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-ink-secondary hover:text-brand-900 hover:bg-surface rounded-xl transition-colors text-left"
                                    >
                                        <FolderInput size={14} className="text-ink-muted" />
                                        {language === 'zh' ? '移到文件夹' : 'Move to Folder'}
                                        <ChevronRight className="ml-auto text-ink-muted" />
                                    </button>
                                    {showFolderPicker === task.taskId && (
                                        <div className="absolute right-full top-0 mr-2 w-44 bg-white border border-surface-border rounded-2xl shadow-xl z-50 p-1 flex flex-col max-h-60 overflow-y-auto">
                                            {folders.length === 0 ? (
                                                <div className="px-4 py-2.5 text-xs text-ink-muted italic">
                                                    {language === 'zh' ? '暂无文件夹' : 'No folders'}
                                                </div>
                                            ) : folders.map(folder => (
                                                <button
                                                    key={folder.folderId}
                                                    onClick={(e) => { e.stopPropagation(); onMoveToFolder(task.taskId, folder.folderId); }}
                                                    className="w-full flex items-center gap-2 px-4 py-2 text-xs font-medium text-ink-secondary hover:text-brand-900 hover:bg-surface rounded-xl transition-colors text-left"
                                                >
                                                    <Folder size={14} className="text-ink-muted shrink-0" />
                                                    <span className="truncate">{displayFolderName(folder.name)}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="h-px bg-surface-border mx-2 my-1 opacity-60"></div>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDelete(task.taskId, displayTitle);
                                    }}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-red-500 hover:bg-red-50 rounded-xl transition-colors text-left"
                                >
                                    <Trash2 size={14} />
                                    {language === 'zh' ? '删除' : 'Delete'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="flex items-start gap-6 pl-3">
                <div className={`mt-1 w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-all duration-500 border ${isProcessing ? 'bg-white border border-surface-border' : 'bg-surface border-surface-border text-ink-muted group-hover:bg-brand-900 group-hover:text-white group-hover:border-brand-900 group-hover:shadow-glow shadow-sm'}`}>
                    {isProcessing ? <Loader2 size={24} className="animate-spin text-brand-900" /> : <FileText size={24} strokeWidth={1.5} />}
                </div>
                <div className="flex-1 min-w-0">
                    {editingTaskId === task.taskId ? (
                        <input
                            ref={taskTitleInputRef}
                            type="text"
                            value={editingTaskTitle}
                            onChange={(e) => setEditingTaskTitle(e.target.value)}
                            onBlur={saveTaskTitle}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') saveTaskTitle();
                                if (e.key === 'Escape') cancelEditingTaskTitle();
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="font-serif font-bold text-lg text-brand-900 mb-2 w-[60%] bg-white border border-surface-divider rounded-lg px-2 py-1 outline-none focus:ring-2 focus:ring-brand-700 focus:border-brand-700"
                        />
                    ) : (
                        <h3
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!isProcessing) {
                                    startEditingTaskTitle(task.taskId, displayTitle);
                                }
                            }}
                            className="font-serif font-bold text-lg truncate transition-colors text-brand-900 mb-2 max-w-[70%] hover:text-brand-700 cursor-text"
                            title={language === 'zh' ? '点击编辑标题' : 'Click to edit title'}
                        >
                            {displayTitle}
                        </h3>
                    )}
                    <p className="text-ink-secondary text-sm leading-relaxed line-clamp-2 mb-6 max-w-4xl font-normal opacity-80">{displaySummary}</p>

                    {isProcessing ? (() => {
                        const percent = task.status === 'completed' ? 100 : calcProgress(task.currentStep ?? 0, task.totalSteps ?? 6);
                        const stepLabel = getStepLabel(task.status || 'pending', language as 'zh' | 'en');
                        return (
                            <div className="w-full mt-7 pr-4">
                                <div className="flex items-center justify-between mb-2.5">
                                    <div className="flex items-center gap-2">
                                        <div className="h-1.5 w-1.5 rounded-full bg-brand-500 animate-pulse"></div>
                                        <span className="text-xs font-bold text-brand-900/70 tracking-tight">{stepLabel}</span>
                                    </div>
                                    <span className="text-sm font-black text-brand-900/20 font-data tabular-nums tracking-tighter">{percent}%</span>
                                </div>
                                <div className="h-1.5 w-full bg-surface-border/40 rounded-full overflow-hidden relative">
                                    <div 
                                        className="absolute inset-y-0 left-0 bg-brand-500/80 rounded-full transition-all duration-1000 ease-out" 
                                        style={{ width: `${percent}%` }}
                                    ></div>
                                </div>
                            </div>
                        );
                    })() : (
                        <div className="flex items-center justify-between mt-auto">
                            <div className="flex items-center gap-6">
                            <div className="flex -space-x-2">
                                {(speakers.length > 0 ? speakers.slice(0, 4) : ['S1', 'S2']).map((speaker, i) => (
                                    <div
                                        key={i}
                                        className="w-7 h-7 rounded-full bg-gradient-to-br from-surface-border to-[#D9E2C8] border-2 border-white flex items-center justify-center text-[10px] font-bold text-brand-900 uppercase shadow-sm"
                                        title={speaker}
                                    >
                                        {speaker.charAt(0)}
                                    </div>
                                ))}
                                {speakers.length > 4 && (
                                    <div className="w-7 h-7 rounded-full bg-surface border-2 border-white flex items-center justify-center text-[9px] font-bold text-ink-secondary shadow-sm">
                                        +{speakers.length - 4}
                                    </div>
                                )}
                            </div>
                            <div className="w-px h-5 bg-surface-divider"></div>
                             <div className="flex gap-2 flex-wrap">
                                {(taskKeywords.length > 0 ? taskKeywords.slice(0, 5) : tags).map((keyword, idx) => (
                                    <span key={idx} className="h-7 flex items-center px-3 text-[10px] font-bold text-ink-dark bg-surface-hover rounded-full border border-surface-divider/50 transition-colors hover:border-brand-900/40">
                                        {keyword}
                                    </span>
                                ))}
                                {taskKeywords.length > 5 && (
                                    <span className="h-7 flex items-center px-3 text-[10px] font-bold text-ink-muted bg-surface rounded-full border border-surface-divider/50">
                                        +{taskKeywords.length - 5}
                                    </span>
                                )}
                            </div>
                            </div>
                            <div className="flex items-center gap-3 text-ink-secondary shrink-0">
                                <div className="flex items-center h-9 gap-2.5 px-3.5 rounded-xl bg-surface border border-surface-border/60 text-xs font-bold text-ink-tertiary shadow-sm">
                                    <Calendar size={13} className="opacity-60" />
                                    <span>{createdAtStr}</span>
                                </div>
                                {durationStr && (
                                    <div className="flex items-center h-9 gap-2.5 px-3.5 rounded-xl bg-surface border border-surface-border/60 text-xs font-bold text-ink-tertiary shadow-sm">
                                        <Clock size={13} className="opacity-60" />
                                        <span>{durationStr}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function StarIcon({ filled }: { filled?: number }) { return <svg width="18" height="18" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>; }
function ChevronRight(props: any) { return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><polyline points="9 18 15 12 9 6"></polyline></svg>; }
